import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Typography,
  Breadcrumbs,
  Link,
  Chip,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';

interface BuildFile {
  name: string;
  fullPath: string;
  url: string;
  size: number;
  lastModified: string;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function GameBuilds() {
  const [password, setPassword] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [builds, setBuilds] = useState<BuildFile[]>([]);

  const handleLogin = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/validate-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        setError('Invalid password');
        setLoading(false);
        return;
      }

      const data = await response.json() as { token: string };
      setToken(data.token);

      // Fetch platforms
      const platformsResponse = await fetch('/api/list-builds', {
        headers: { 'x-builds-token': data.token },
      });
      const platformsData = await platformsResponse.json() as { platforms: string[] };
      setPlatforms(platformsData.platforms || []);
    } catch (err) {
      setError('Failed to connect. Please try again.');
    }
    setLoading(false);
  }, [password]);

  const handleSelectPlatform = useCallback(async (platform: string) => {
    if (!token) return;
    setLoading(true);
    setSelectedPlatform(platform);
    try {
      const response = await fetch(`/api/list-builds?platform=${encodeURIComponent(platform)}`, {
        headers: { 'x-builds-token': token },
      });
      const data = await response.json() as { builds: BuildFile[] };
      setBuilds(data.builds || []);
    } catch (err) {
      setError('Failed to load builds.');
    }
    setLoading(false);
  }, [token]);

  const handleBack = useCallback(() => {
    setSelectedPlatform(null);
    setBuilds([]);
  }, []);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  }, [handleLogin]);

  // Password screen
  if (!token) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <Card sx={{ maxWidth: 400, width: '100%', mx: 2 }}>
          <CardContent>
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              <LockIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
              <Typography variant="h5" component="h1">
                Game Builds
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Enter the password to access game builds.
              </Typography>
              <TextField
                fullWidth
                type="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword((e.target as HTMLInputElement).value)}
                onKeyPress={handleKeyPress}
                error={!!error}
                helperText={error}
                autoFocus
              />
              <Button
                fullWidth
                variant="contained"
                onClick={handleLogin}
                disabled={loading || !password}
              >
                {loading ? <CircularProgress size={24} /> : 'Access Builds'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  // Build files list
  if (selectedPlatform) {
    return (
      <Box sx={{ maxWidth: 700, mx: 'auto', p: 2 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            underline="hover"
            onClick={handleBack}
            sx={{ cursor: 'pointer' }}
          >
            Platforms
          </Link>
          <Typography color="text.primary">{selectedPlatform}</Typography>
        </Breadcrumbs>

        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <IconButton onClick={handleBack} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">{selectedPlatform} Builds</Typography>
        </Box>

        {builds.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No builds available for {selectedPlatform} yet.
            </Typography>
          </Paper>
        ) : (
          <Paper>
            <List>
              {builds.map((build, index) => (
                <ListItem
                  key={build.fullPath}
                  divider={index < builds.length - 1}
                  secondaryAction={
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      href={build.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download
                    </Button>
                  }
                >
                  <ListItemText
                    primary={build.name}
                    secondary={
                      <Box component="span" display="flex" gap={1} alignItems="center" flexWrap="wrap">
                        <Chip label={formatFileSize(build.size)} size="small" variant="outlined" />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(build.lastModified)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Box>
    );
  }

  // Platform selection
  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Game Builds
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select a platform to view available builds.
      </Typography>

      {platforms.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No builds have been uploaded yet.
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <List>
            {platforms.map((platform, index) => (
              <ListItem
                key={platform}
                divider={index < platforms.length - 1}
                onClick={() => handleSelectPlatform(platform)}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <ListItemIcon>
                  <FolderIcon />
                </ListItemIcon>
                <ListItemText primary={platform} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}
